/**
 * BR TABLE — FRONTEND JS
 * Checkbox dropdown filters (date + custom) + search + sort + pagination
 */
(function () {
    'use strict';

    const CARDS_PER_PAGE = 9;

    /* ═══════════════════════════════════════════════════════════
       BUILD ONE DROPDOWN FILTER
       Creates: [ Filter by: Year ▾ ] [ ✕ Clear ]
                ┌─────────────────────┐
                │ ☐ 2023              │
                │ ☑ 2024              │
                │ ☐ 2025              │
                └─────────────────────┘
    ═══════════════════════════════════════════════════════════ */
    function buildDropdown(wrapper, label, options, onChange) {
        /* ── State ── */
        let selected = new Set(); // set of selected values
        let open     = false;

        /* ── Trigger button ── */
        const trigger = document.createElement('button');
        trigger.type      = 'button';
        trigger.className = 'br-dropdown-trigger';
        trigger.setAttribute('aria-haspopup', 'listbox');
        trigger.setAttribute('aria-expanded', 'false');

        const triggerLabel = document.createElement('span');
        triggerLabel.className   = 'br-dropdown-trigger-label';
        triggerLabel.textContent = label;

        const triggerArrow = document.createElement('span');
        triggerArrow.className   = 'br-dropdown-arrow';
        triggerArrow.textContent = '▾';
        triggerArrow.setAttribute('aria-hidden', 'true');

        trigger.appendChild(triggerLabel);
        trigger.appendChild(triggerArrow);

        /* ── Clear button (hidden by default) ── */
        const clearBtn = document.createElement('button');
        clearBtn.type      = 'button';
        clearBtn.className = 'br-dropdown-clear';
        clearBtn.innerHTML = '✕ Clear';
        clearBtn.style.display = 'none';

        /* ── Panel ── */
        const panel = document.createElement('div');
        panel.className = 'br-dropdown-panel';
        panel.setAttribute('role', 'listbox');
        panel.setAttribute('aria-multiselectable', 'true');
        panel.style.display = 'none';

        /* ── Checkbox list ── */
        options.forEach(({ label: optLabel, value }) => {
            const item = document.createElement('label');
            item.className = 'br-dropdown-item';

            const cb = document.createElement('input');
            cb.type  = 'checkbox';
            cb.value = value;
            cb.setAttribute('aria-label', optLabel);

            cb.addEventListener('change', () => {
                if (cb.checked) selected.add(value);
                else            selected.delete(value);

                updateClearBtn();
                onChange([...selected]);
            });

            const text = document.createElement('span');
            text.textContent = optLabel;

            item.appendChild(cb);
            item.appendChild(text);
            panel.appendChild(item);
        });

        /* ── Helpers ── */
        const updateClearBtn = () => {
            clearBtn.style.display = selected.size > 0 ? '' : 'none';
            trigger.classList.toggle('has-selection', selected.size > 0);
        };

        const openPanel = () => {
            open = true;
            panel.style.display = 'block';
            trigger.setAttribute('aria-expanded', 'true');
            trigger.classList.add('is-open');
        };

        const closePanel = () => {
            open = false;
            panel.style.display = 'none';
            trigger.setAttribute('aria-expanded', 'false');
            trigger.classList.remove('is-open');
        };

        /* ── Toggle on trigger click ── */
        trigger.addEventListener('click', (e) => {
            e.stopPropagation();
            open ? closePanel() : openPanel();
        });

        /* ── Clear ── */
        clearBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            selected.clear();
            panel.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
                cb.checked = false;
            });
            updateClearBtn();
            closePanel();
            onChange([]);
        });

        /* ── Close on outside click ── */
        document.addEventListener('click', (e) => {
            if (open && !wrapper.contains(e.target)) closePanel();
        });

        /* ── Close on Escape ── */
        document.addEventListener('keydown', (e) => {
            if (open && e.key === 'Escape') closePanel();
        });

        /* ── Assemble ── */
        wrapper.appendChild(trigger);
        wrapper.appendChild(clearBtn);
        wrapper.appendChild(panel);
    }

    /* ═══════════════════════════════════════════════════════════
       DATE HELPERS
    ═══════════════════════════════════════════════════════════ */
    const MONTH_NAMES = {
        january:1, february:2, march:3, april:4, may:5, june:6,
        july:7, august:8, september:9, october:10, november:11, december:12,
        jan:1, feb:2, mar:3, apr:4, jun:6, jul:7, aug:8,
        sep:9, oct:10, nov:11, dec:12,
    };

    const parseDate = (text) => {
        if (!text) return null;
        let m = text.match(/(\d{4})-(\d{2})-(\d{2})/);
        if (m) return { year: parseInt(m[1]), month: parseInt(m[2]) };
        m = text.match(/(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);
        if (m) { const mo = MONTH_NAMES[m[2].toLowerCase()]; if (mo) return { year: parseInt(m[3]), month: mo }; }
        m = text.match(/([A-Za-z]+)\s+(\d{1,2})[,\s]+(\d{4})/);
        if (m) { const mo = MONTH_NAMES[m[1].toLowerCase()]; if (mo) return { year: parseInt(m[3]), month: mo }; }
        m = text.match(/\b(\d{4})\b/);
        if (m) return { year: parseInt(m[1]), month: null };
        return null;
    };

    const dateKeyFromText = (text, mode) => {
        const d = parseDate(text);
        if (!d) return '';
        if (mode === 'month' && d.month)
            return `${d.year}-${String(d.month).padStart(2, '0')}`;
        return String(d.year);
    };

    const dateKeyLabel = (key, mode) => {
        if (mode !== 'month') return key;
        const [year, month] = key.split('-');
        const name = new Date(parseInt(year), parseInt(month) - 1, 1)
            .toLocaleString('default', { month: 'long' });
        return `${name} ${year}`;
    };

    /* ═══════════════════════════════════════════════════════════
       INIT ONE TABLE
    ═══════════════════════════════════════════════════════════ */
    function initBrTable(container) {
        const table       = container.querySelector('.br-table');
        const toolbar     = container.querySelector('.br-table-toolbar');
        const pagination  = container.querySelector('.br-table-pagination');
        const searchInput = container.querySelector('.br-table-search-input');

        if (!table || table.dataset.brInit === '1') return;
        table.dataset.brInit = '1';

        if (window.innerWidth <= 768) table.setAttribute('data-view', 'cards');

        /* ── Shared filter state ─────────────────────────────── */
        let searchQuery        = '';
        let activeDateValues   = [];   // array (multi-select)
        let activeCustomValues = [];   // array (multi-select)
        let currentPage        = 0;

        /* ─────────────────────────────────────────────────────
           HELPERS
        ───────────────────────────────────────────────────── */
        const getCell = (row, colIndex) =>
            row.querySelectorAll('.br-table-cell')[colIndex];

        const show = (row) => { row.classList.remove('is-hidden'); row.style.display = ''; };
        const hide = (row) => { row.classList.add('is-hidden');    row.style.display = 'none'; };

        /* ─────────────────────────────────────────────────────
           LABELS (cards mode)
           Replaces <br> and paragraph breaks with " - " so the
           label stays on a single line in cards mode.
        ───────────────────────────────────────────────────── */
        const updateLabels = () => {
            const headerRow = table.querySelector('.br-table-row.is-header');
            if (!headerRow) return;

            const labels = Array.from(headerRow.querySelectorAll('.br-table-cell'))
                .map((cell) => {
                    const html = cell.innerHTML || '';
                    return html
                        .replace(/<br\s*\/?>/gi, ' - ')         // <br>  → " - "
                        .replace(/<\/p>\s*<p[^>]*>/gi, ' - ')   // </p><p> → " - "
                        .replace(/<[^>]+>/g, '')                // strip remaining HTML tags
                        .replace(/\s+/g, ' ')                   // collapse whitespaces
                        .trim();
                });

            table.querySelectorAll('.br-table-row:not(.is-header)').forEach((row) => {
                row.querySelectorAll('.br-table-cell').forEach((cell, i) => {
                    if (labels[i]) cell.setAttribute('data-label', labels[i]);
                });
            });
        };

        /* ─────────────────────────────────────────────────────
           SECTION HEADER DETECTION
           A "section header" row is a body row where only 1 cell
           is visible (the others are merged/hidden). It acts as a
           group label and must be shown if any row in its group
           passes the active filters.
        ───────────────────────────────────────────────────── */
        const isSectionHeader = (row) => {
            // Explicit: row was marked as group header in the editor
            if (row.classList.contains('is-group-header')) return true;
            // Fallback: row has 2+ cells but only 1 is visible (merged/colspan)
            const cells   = row.querySelectorAll('.br-table-cell');
            const visible = Array.from(cells).filter(
                (c) => c.style.display !== 'none' && c.getAttribute('data-hidden') !== 'true'
            );
            return cells.length >= 2 && visible.length <= 1;
        };

        /* ─────────────────────────────────────────────────────
           MASTER DISPLAY — applies all active filters
           Supports grouped rows (data-grouped="true"):
           When filtering, if a section header label matches,
           all child rows of that group are shown.
        ───────────────────────────────────────────────────── */
        // Auto-detect: table is grouped if it contains any is-group-header rows
        // (no need for data-grouped attribute — works automatically)
        const isGrouped = table.querySelectorAll('.br-table-row.is-group-header').length > 0;

        const handleDisplay = () => {
            const view      = table.getAttribute('data-view');
            const dateCol   = parseInt(table.dataset.dateCol   ?? '-1');
            const customCol = parseInt(table.dataset.customCol ?? '-1');
            const dateMode  = table.dataset.dateMode || 'year';

            const allBodyRows = Array.from(
                table.querySelectorAll('.br-table-row:not(.is-header)')
            );

            // ── Build groups when table has grouped rows ──────────
            // groups = [{ header: rowEl|null, children: [rowEl, ...] }, ...]
            const groups = [];
            if (isGrouped) {
                let current = { header: null, children: [] };
                allBodyRows.forEach((row) => {
                    if (isSectionHeader(row)) {
                        if (current.header !== null || current.children.length > 0) {
                            groups.push(current);
                        }
                        current = { header: row, children: [] };
                    } else {
                        current.children.push(row);
                    }
                });
                groups.push(current);
            }

            // ── Row-level filter test ──
            // In grouped mode, custom filter is handled at group level (headerMatchesFilter),
            // so we skip it here to avoid false positives on child rows.
            const rowPasses = (row) => {
                if (searchQuery &&
                    !row.textContent.toLowerCase().includes(searchQuery.toLowerCase())) {
                    return false;
                }
                if (activeDateValues.length > 0 && dateCol >= 0) {
                    const key = dateKeyFromText(
                        getCell(row, dateCol)?.textContent?.trim() || '', dateMode
                    );
                    if (!activeDateValues.includes(key)) return false;
                }
                // Custom filter: only apply at row level when NOT in grouped mode
                if (!isGrouped && activeCustomValues.length > 0 && customCol >= 0) {
                    const text = getCell(row, customCol)?.textContent?.toLowerCase() || '';
                    if (!activeCustomValues.some((v) => text.includes(v.toLowerCase()))) return false;
                }
                return true;
            };

            // ── Header label match (for grouped mode) ──────────────
            // Exact match against the group header's full text content,
            // which is the same value used when building the dropdown options.
            const headerMatchesFilter = (headerRow) => {
                if (!headerRow) return false;
                if (activeCustomValues.length === 0) return false;
                const text = headerRow.textContent.replace(/\s+/g, ' ').trim();
                return activeCustomValues.some((v) => v.trim() === text);
            };

            const filteredRows = [];

            if (isGrouped && groups.length > 0) {
                const customActive = activeCustomValues.length > 0;

                groups.forEach(({ header, children }) => {
                    const headerMatches = headerMatchesFilter(header);

                    if (customActive) {
                        // Custom filter is group-level only:
                        // show group if header matches, hide entirely if not
                        if (headerMatches) {
                            if (header) show(header);
                            // Still apply search filter on children
                            children.forEach((row) => {
                                if (rowPasses(row)) { show(row); filteredRows.push(row); }
                                else hide(row);
                            });
                        } else {
                            if (header) hide(header);
                            children.forEach(hide);
                        }
                    } else {
                        // No custom filter — apply search/date on each child row
                        const visibleChildren = children.filter(rowPasses);
                        if (visibleChildren.length > 0) {
                            if (header) show(header);
                            children.forEach((row) => {
                                if (visibleChildren.includes(row)) { show(row); filteredRows.push(row); }
                                else hide(row);
                            });
                        } else {
                            if (header) hide(header);
                            children.forEach(hide);
                        }
                    }
                });
            } else {
                // ── Non-grouped mode (original logic) ──────────────
                const rowResults = new Map();

                allBodyRows.forEach((row) => {
                    if (isSectionHeader(row)) { rowResults.set(row, null); return; }
                    rowResults.set(row, rowPasses(row));
                });

                let pendingSection = null;
                allBodyRows.forEach((row) => {
                    if (isSectionHeader(row)) {
                        pendingSection = row;
                        hide(row);
                        return;
                    }
                    if (rowResults.get(row)) {
                        show(row);
                        if (pendingSection) { show(pendingSection); pendingSection = null; }
                        filteredRows.push(row);
                    } else {
                        hide(row);
                    }
                });
            }

            // Pagination (cards only)
            if (view === 'cards') {
                const totalPages = Math.ceil(filteredRows.length / CARDS_PER_PAGE);
                if (totalPages <= 1) {
                    pagination.style.display = 'none';
                    filteredRows.forEach(show);
                } else {
                    pagination.style.display = 'flex';
                    renderPagination(filteredRows, totalPages);
                }
            } else {
                pagination.style.display = 'none';
            }
        };

        /* ─────────────────────────────────────────────────────
           DATE FILTER
        ───────────────────────────────────────────────────── */
        const dateWrapper = container.querySelector('.br-dropdown-filter[data-filter="date"]');
        if (dateWrapper) {
            const colIndex = parseInt(dateWrapper.dataset.column ?? '0');
            const mode     = dateWrapper.dataset.mode || 'year';

            table.dataset.dateCol  = colIndex;
            table.dataset.dateMode = mode;

            // Collect unique date keys
            const seen = new Map();
            table.querySelectorAll('.br-table-row:not(.is-header)').forEach((row) => {
                const text = getCell(row, colIndex)?.textContent?.trim() || '';
                const key  = dateKeyFromText(text, mode);
                if (key && !seen.has(key)) seen.set(key, dateKeyLabel(key, mode));
            });

            if (seen.size > 0) {
                const sortedKeys = [...seen.keys()].sort().reverse();
                const options    = sortedKeys.map((k) => ({ label: seen.get(k), value: k }));
                const label      = 'Filter by';

                buildDropdown(dateWrapper, label, options, (vals) => {
                    activeDateValues = vals;
                    currentPage      = 0;
                    handleDisplay();
                });
            } else {
                dateWrapper.style.display = 'none';
            }
        }

        /* ─────────────────────────────────────────────────────
           CUSTOM FILTER
           When the table has grouped rows (data-grouped="true"),
           options are auto-built from is-group-header rows so
           the filter always matches perfectly — no manual entry
           needed. Falls back to manually-defined options otherwise.
        ───────────────────────────────────────────────────── */
        const customWrapper = container.querySelector('.br-dropdown-filter[data-filter="custom"]');
        if (customWrapper) {
            const label = customWrapper.dataset.label || 'Filter by';

            let options = [];

            if (isGrouped) {
                // ── AUTO-BUILD from group header rows ──────────────
                // Each is-group-header row becomes a filter option.
                // value = exact trimmed text of the header row.
                table.querySelectorAll('.br-table-row.is-group-header').forEach((row) => {
                    const text = row.textContent.replace(/\s+/g, ' ').trim();
                    if (text && !options.find((o) => o.value === text)) {
                        options.push({ label: text, value: text });
                    }
                });
            } else {
                // ── MANUAL options from backend JSON ───────────────
                const colIndex = parseInt(customWrapper.dataset.column ?? '0');
                table.dataset.customCol = colIndex;
                try {
                    const raw = customWrapper.querySelector('.br-filter-options-data')?.textContent;
                    if (raw) options = JSON.parse(raw);
                } catch { /* ignore */ }
            }

            if (options.length > 0) {
                buildDropdown(customWrapper, label, options, (vals) => {
                    activeCustomValues = vals;
                    currentPage        = 0;
                    handleDisplay();
                });
            } else {
                // No options found (grouped mode but no group headers yet, or no manual options)
                customWrapper.style.display = 'none';
            }
        }

        /* ─────────────────────────────────────────────────────
           SEARCH
        ───────────────────────────────────────────────────── */
        if (searchInput) {
            searchInput.addEventListener('input', (e) => {
                searchQuery = e.target.value;
                currentPage = 0;
                handleDisplay();
            });
        }

        /* ─────────────────────────────────────────────────────
           VIEW SWITCH
        ───────────────────────────────────────────────────── */
        if (toolbar) {
            const switchBtns = toolbar.querySelectorAll('.br-table-switch-btn');
            switchBtns.forEach((btn) => {
                btn.addEventListener('click', () => {
                    if (window.innerWidth <= 768) {
                        table.setAttribute('data-view', 'cards');
                        switchBtns.forEach((b) => b.classList.remove('is-active'));
                        switchBtns[1]?.classList.add('is-active');
                    } else {
                        table.setAttribute('data-view', btn.dataset.view);
                        switchBtns.forEach((b) => b.classList.remove('is-active'));
                        btn.classList.add('is-active');
                    }
                    currentPage = 0;
                    handleDisplay();
                });
            });
            if (window.innerWidth <= 768) {
                switchBtns.forEach((b) => (b.style.display = 'none'));
            }
        }

        /* ─────────────────────────────────────────────────────
           FRONTEND SORT
        ───────────────────────────────────────────────────── */
        if (table.dataset.sortable === 'true') {
            const headerRow = table.querySelector('.br-table-row.is-header');
            if (headerRow) {
                let sortState   = { col: null, dir: 'asc' };
                const headerCells = headerRow.querySelectorAll('.br-table-cell');
                const getCellTxt  = (cell) => (cell?.textContent || '').replace(/\s+/g, ' ').trim();

                const doSort = (colIndex) => {
                    const newDir = (sortState.col === colIndex && sortState.dir === 'asc') ? 'desc' : 'asc';
                    sortState = { col: colIndex, dir: newDir };

                    const bodyRows = Array.from(table.querySelectorAll('.br-table-row:not(.is-header)'));
                    bodyRows.sort((a, b) => {
                        const va = getCellTxt(a.querySelectorAll('.br-table-cell')[colIndex]);
                        const vb = getCellTxt(b.querySelectorAll('.br-table-cell')[colIndex]);
                        const na = parseFloat(va.replace(/[^0-9.-]/g, ''));
                        const nb = parseFloat(vb.replace(/[^0-9.-]/g, ''));
                        if (!isNaN(na) && !isNaN(nb)) return newDir === 'asc' ? na - nb : nb - na;
                        const cmp = va.localeCompare(vb, undefined, { sensitivity: 'base' });
                        return newDir === 'asc' ? cmp : -cmp;
                    });
                    bodyRows.forEach((row) => table.appendChild(row));

                    headerCells.forEach((c, i) => {
                        c.querySelector('.br-sort-indicator')?.remove();
                        c.classList.remove('is-sort-asc', 'is-sort-desc');
                        c.setAttribute('aria-sort', 'none');
                        if (i === colIndex) {
                            c.classList.add(newDir === 'asc' ? 'is-sort-asc' : 'is-sort-desc');
                            c.setAttribute('aria-sort', newDir === 'asc' ? 'ascending' : 'descending');
                            const ind = document.createElement('span');
                            ind.className   = 'br-sort-indicator';
                            ind.textContent = newDir === 'asc' ? ' ↑' : ' ↓';
                            ind.setAttribute('aria-hidden', 'true');
                            c.appendChild(ind);
                        }
                    });
                    handleDisplay();
                };

                headerCells.forEach((cell, i) => {
                    cell.style.cursor = 'pointer';
                    cell.setAttribute('role', 'button');
                    cell.setAttribute('tabindex', '0');
                    cell.setAttribute('aria-sort', 'none');
                    cell.addEventListener('click', () => doSort(i));
                    cell.addEventListener('keydown', (e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); doSort(i); }
                    });
                });
            }
        }

        /* ─────────────────────────────────────────────────────
           PAGINATION
        ───────────────────────────────────────────────────── */
        const renderPagination = (rows, totalPages) => {
            const goTo = (idx) => {
                const start = idx * CARDS_PER_PAGE;
                rows.forEach((row, i) => {
                    const v = i >= start && i < start + CARDS_PER_PAGE;
                    row.classList.toggle('is-hidden', !v);
                    row.style.display = v ? '' : 'none';
                });

                pagination.innerHTML = '';
                const mkBtn = (text, disabled, onClick) => {
                    const b = document.createElement('button');
                    b.className   = 'br-pagination-btn';
                    b.textContent = text;
                    b.disabled    = disabled;
                    b.addEventListener('click', onClick);
                    return b;
                };

                pagination.appendChild(mkBtn('<', idx === 0, () => { currentPage--; goTo(currentPage); window.scrollTo({ top: 0, behavior: 'smooth' }); }));

                const range = totalPages <= 5
                    ? Array.from({ length: totalPages }, (_, i) => i)
                    : (() => {
                        const r = [0];
                        if (idx > 2) r.push('…');
                        for (let i = Math.max(1, idx - 1); i <= Math.min(totalPages - 2, idx + 1); i++) r.push(i);
                        if (idx < totalPages - 3) r.push('…');
                        r.push(totalPages - 1);
                        return r;
                    })();

                range.forEach((item) => {
                    if (item === '…') {
                        const s = document.createElement('span');
                        s.textContent   = '…';
                        s.style.padding = '0 4px';
                        pagination.appendChild(s);
                    } else {
                        const b = mkBtn(item + 1, false, () => { currentPage = item; goTo(currentPage); window.scrollTo({ top: 0, behavior: 'smooth' }); });
                        if (item === idx) b.classList.add('is-active');
                        pagination.appendChild(b);
                    }
                });

                pagination.appendChild(mkBtn('>', idx === totalPages - 1, () => { currentPage++; goTo(currentPage); window.scrollTo({ top: 0, behavior: 'smooth' }); }));
            };
            goTo(0);
        };

        /* ─────────────────────────────────────────────────────
           COLUMN HOVER HIGHLIGHT
        ───────────────────────────────────────────────────── */
        table.addEventListener('mouseover', (e) => {
            if (table.getAttribute('data-view') !== 'table') return;
            const cell = e.target.closest('.br-table-cell');
            if (!cell) return;
            const row   = cell.closest('.br-table-row');
            const index = Array.from(row.querySelectorAll('.br-table-cell')).indexOf(cell);
            table.style.setProperty('--br-hover-col', index);
        });
        table.addEventListener('mouseleave', () => table.style.setProperty('--br-hover-col', '-1'));

        /* ── BOOT ── */
        updateLabels();
        handleDisplay();
    }

    /* ═══════════════════════════════════════════════════════════
       SCAN
    ═══════════════════════════════════════════════════════════ */
    function scan() {
        document.querySelectorAll('.br-table-container').forEach(initBrTable);
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
    else scan();
    window.addEventListener('load', scan);

})();